import { Observable, Subscription } from 'rxjs';
import {
  GRAPHQL_WS_RETRY,
  graphqlWsRetryDelayMs,
  logGraphqlSubscriptionEvent,
} from './graphql-ws-retry.util';
import { GraphqlWsLifecycleService } from './graphql-ws-lifecycle.service';

export interface GraphqlSubscriptionRetryPolicy {
  initialDelayMs?: number;
  maxDelayMs?: number;
  maxAttempts?: number;
}

/**
 * Preferred helper for long-lived GraphQL subscriptions.
 *
 * Callers own *when* to run via {@link shouldRun} and explicit {@link start}/{@link stop}.
 * Backoff and attempt budget default to {@link GRAPHQL_WS_RETRY} (aligned with graphql-ws).
 */
export class ReconnectingGraphqlSubscription<T> {
  private streamSubscription?: Subscription;
  private transportOpenedSubscription?: Subscription;
  private retryTimeout?: ReturnType<typeof setTimeout>;
  private retryAttempt = 0;
  private started = false;

  constructor(
    private readonly options: {
      scope: string;
      shouldRun: () => boolean;
      createStream: () => Observable<T>;
      onValue: (value: T) => void;
      onStreamError?: (error: unknown) => void;
      retry?: GraphqlSubscriptionRetryPolicy;
    },
    wsLifecycle?: GraphqlWsLifecycleService
  ) {
    if (wsLifecycle) {
      this.transportOpenedSubscription = wsLifecycle.opened$.subscribe(() => {
        this.retryAttempt = 0;
        this.clearRetryTimer();
        this.attachIfNeeded();
      });
    }
  }

  start(): void {
    this.started = true;
    this.attachIfNeeded();
  }

  stop(): void {
    this.started = false;
    this.clearRetryTimer();
    this.retryAttempt = 0;
    this.teardownStream();
  }

  isAttached(): boolean {
    return this.streamSubscription !== undefined;
  }

  private canAttach(): boolean {
    return this.started && this.options.shouldRun();
  }

  private attachIfNeeded(): void {
    if (!this.canAttach() || this.streamSubscription) {
      return;
    }

    this.streamSubscription = this.options.createStream().subscribe({
      next: (value) => {
        this.retryAttempt = 0;
        this.options.onValue(value);
      },
      error: (error: unknown) => {
        this.options.onStreamError?.(error);
        logGraphqlSubscriptionEvent(this.options.scope, 'stream error', error);
        this.teardownStream();
        this.scheduleRetry();
      },
      complete: () => {
        this.teardownStream();
        this.scheduleRetry();
      },
    });
  }

  private teardownStream(): void {
    this.streamSubscription?.unsubscribe();
    this.streamSubscription = undefined;
  }

  private scheduleRetry(): void {
    if (!this.canAttach() || this.retryTimeout !== undefined) {
      return;
    }

    const initialDelayMs =
      this.options.retry?.initialDelayMs ?? GRAPHQL_WS_RETRY.initialDelayMs;
    const maxDelayMs =
      this.options.retry?.maxDelayMs ?? GRAPHQL_WS_RETRY.maxDelayMs;
    const maxAttempts =
      this.options.retry?.maxAttempts ?? GRAPHQL_WS_RETRY.maxAttempts;

    if (this.retryAttempt >= maxAttempts) {
      logGraphqlSubscriptionEvent(
        this.options.scope,
        `retry budget exhausted (${maxAttempts} attempts); waiting for WebSocket reopen or stop/start`
      );
      return;
    }

    const delayMs = graphqlWsRetryDelayMs(
      this.retryAttempt,
      initialDelayMs,
      maxDelayMs
    );
    this.retryAttempt += 1;

    logGraphqlSubscriptionEvent(
      this.options.scope,
      `retry in ${delayMs}ms (attempt ${this.retryAttempt}/${maxAttempts})`
    );

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = undefined;
      this.attachIfNeeded();
    }, delayMs);
  }

  private clearRetryTimer(): void {
    if (this.retryTimeout !== undefined) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = undefined;
    }
  }
}
